import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ChefHat, Award, Heart, Users } from "lucide-react";
import { useLanguage } from "@/LanguageContext";
import { API } from "@/App";
import axios from "axios";

const AboutPage = () => {
  const { t } = useLanguage();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/about-content`);
      setContent(response.data);
    } catch (error) {
      console.error("Error fetching about content:", error);
      setContent({
        story_title: "Autentisk Indisk Smak i Harstad",
        story_text1: "Masala Indisk Restaurant og Bar ble grunnlagt med en enkel visjon.",
        story_text2: "Hver rett vi serverer er laget med kjærlighet.",
        food_section_title: "Tradisjonelle Oppskrifter",
        food_section_text: "Vi tilbyr klassiske indiske retter.",
        food_image_url: "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=800",
        values_title: "Våre Verdier",
        value1_title: "Autentisk", value1_text: "Ekte oppskrifter",
        value2_title: "Kvalitet", value2_text: "Ferske ingredienser",
        value3_title: "Service", value3_text: "Vennlig betjening"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#fdf8f0' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: '#721111' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
      <Navbar />

      {/* Hero */}
      <div
        className="relative py-20 flex items-center justify-center overflow-hidden mandala-pattern"
        style={{
          background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)',
        }}
      >
        <div className="text-center z-10 px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="indian-divider mb-4"
          >
            <span className="text-sm uppercase tracking-widest" style={{ color: '#d4af37' }}>Om Oss</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-serif-display font-semibold italic mb-4"
            style={{ color: '#d4af37' }}
            data-testid="about-page-title"
          >
            {t("about_title")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{ color: '#f5e6d3' }}
          >
            {t("about_subtitle")}
          </motion.p>
        </div>
      </div>

      {/* Decorative Divider */}
      <div className="h-2" style={{ background: 'linear-gradient(90deg, #721111, #d4af37, #721111)' }}></div>

      {/* Our Story */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="indian-divider mb-6">
              <span className="text-sm uppercase tracking-widest" style={{ color: '#8b4513' }}>Vår Historie</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif-display font-semibold italic mb-8" style={{ color: '#2d1810' }}>
              {content?.story_title || "Autentisk Indisk Smak i Harstad"}
            </h2>
            <p className="text-base mb-6 leading-relaxed" style={{ color: '#4a3728' }}>
              {content?.story_text1 || ""}
            </p>
            <p className="text-base mb-6 leading-relaxed" style={{ color: '#4a3728' }}>
              {content?.story_text2 || ""}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Food Image Section */}
      <section 
        className="py-20 mandala-pattern"
        style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="indian-divider mb-6">
                <span className="text-sm uppercase tracking-widest" style={{ color: '#d4af37' }}>Vår Mat</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-serif-display font-semibold italic mb-4" style={{ color: '#d4af37' }}>
                {content?.food_section_title || "Tradisjonelle Oppskrifter"}
              </h2>
              <p className="mb-4 leading-relaxed" style={{ color: '#f5e6d3' }}>
                {content?.food_section_text || ""}
              </p>
              <div className="flex gap-6 mt-6">
                <div className="flex items-center gap-2 text-sm" style={{ color: '#f5e6d3' }}>
                  <Award className="h-5 w-5" style={{ color: '#d4af37' }} />
                  <span>Ferske Ingredienser</span>
                </div>
                <div className="flex items-center gap-2 text-sm" style={{ color: '#f5e6d3' }}>
                  <ChefHat className="h-5 w-5" style={{ color: '#d4af37' }} />
                  <span>Autentiske Krydder</span>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="rounded-xl overflow-hidden shadow-2xl" style={{ border: '4px solid #d4af37' }}>
                <img
                  src={content?.food_image_url || "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=800"}
                  alt="Indisk mat"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Decorative Divider */}
      <div className="h-2" style={{ background: 'linear-gradient(90deg, #721111, #d4af37, #721111)' }}></div>

      {/* Values Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="indian-divider mb-6">
              <span className="text-sm uppercase tracking-widest" style={{ color: '#8b4513' }}>Verdier</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif-display font-semibold italic" style={{ color: '#2d1810' }}>
              {t("about_values_title")}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Heart className="h-8 w-8" />,
                title: t("about_val_auth"),
                description: t("about_val_auth_desc")
              },
              {
                icon: <Users className="h-8 w-8" />,
                title: t("about_val_comm"),
                description: t("about_val_comm_desc")
              },
              {
                icon: <Award className="h-8 w-8" />,
                title: t("about_val_qual"),
                description: t("about_val_qual_desc")
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 text-center dish-card"
                style={{ border: '2px solid #b27f47' }}
              >
                <div 
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                  style={{ background: '#721111' }}
                >
                  <span style={{ color: '#d4af37' }}>{value.icon}</span>
                </div>
                <h3 className="text-xl font-serif-display font-semibold mb-3" style={{ color: '#2d1810' }}>
                  {value.title}
                </h3>
                <p className="text-sm" style={{ color: '#4a3728' }}>{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 mandala-pattern"
        style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-serif-display font-semibold italic mb-4" style={{ color: '#d4af37' }}>
              {t("about_cta_title")}
            </h2>
            <p className="mb-8 max-w-2xl mx-auto" style={{ color: '#f5e6d3' }}>
              {t("about_cta_desc")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking">
                <Button className="btn-gold rounded-full font-bold px-10 py-6 text-lg transition-transform hover:scale-105">
                  Reserver Bord
                </Button>
              </Link>
              <Link to="/menu">
                <Button 
                  className="rounded-full font-bold px-10 py-6 text-lg transition-transform hover:scale-105"
                  style={{ 
                    background: 'transparent', 
                    border: '2px solid #d4af37', 
                    color: '#d4af37' 
                  }}
                >
                  Se Meny
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
